import {isNil} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';

import {isNotNilOrEmpty, Nullish, useOnMount} from 'shared/web';

import {useCallPanel} from '../../hooks/useCallPanel';
import {usePairableCall} from '../../hooks/usePairableCall';
import {useTelephonyCallAvailability} from '../../hooks/useTelephonyCallAvailability';
import {TelephonyCallPairableRecord} from '../../types/TelephonyCallPairableRecord';

type PairableRecordLabel = Omit<TelephonyCallPairableRecord, 'openedAt'>;

interface TelephonyCallRecordVisitProps extends Omit<PairableRecordLabel, 'details'> {
  /** Empty and missing values (data still loading) are left out. */
  details: (string | Nullish)[];
}

/**
 * Renders nothing. Mount it on a record detail page: while the viewer has a call to pair (live, or
 * ended and not dismissed), the record joins the after-call card's "opened during the call" list,
 * labelled as the page shows it. A label that changes (data still loading) updates the entry.
 */
export function TelephonyCallRecordVisit(props: TelephonyCallRecordVisitProps) {
  const {isAllowed} = useTelephonyCallAvailability();
  const {state} = usePairableCall();
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  // The tab may still hold another user's call (logged out without closing the card).
  if (!isAllowed || isNil(state) || state.isDismissed || state.userId !== currentUser?.id) {
    return null;
  }

  const record = {...props, details: props.details.filter(isNotNilOrEmpty)};

  return (
    <RecordVisitWriter
      key={[
        state.callId,
        record.resourceId,
        record.recordId,
        record.title,
        record.customerName,
        ...record.details,
      ].join()}
      callId={state.callId}
      record={record}
    />
  );
}

interface RecordVisitWriterProps {
  callId: string;
  record: PairableRecordLabel;
}

function RecordVisitWriter(props: RecordVisitWriterProps) {
  const {store} = usePairableCall();
  const {store: panelStore} = useCallPanel();
  const recordKey = `${props.record.resourceId}-${props.record.recordId}`;

  // While mounted, the record is the call panel's "This page" row.
  useOnMount(
    () => {
      store.addRecord(props.callId, {...props.record, openedAt: new Date().toISOString()});
      panelStore.enterRecord(recordKey);
    },
    () => {
      panelStore.leaveRecord(recordKey);
    }
  );

  return null;
}
